import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Empty, Input, InputNumber, Modal, Row, Select, Space, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useInventoryContext } from './InventoryContext';
import { useLanguage } from './LanguageContext';

const { Text } = Typography;

interface DeductionRow {
  key: number;
  inventoryId?: string;
  quantity: number;
}

interface ManualInventoryDeductionModalProps {
  open: boolean;
  patient: any;
  onClose: () => void;
}

const newRow = (key: number): DeductionRow => ({ key, quantity: 1 });

const ManualInventoryDeductionModal: React.FC<ManualInventoryDeductionModalProps> = ({
  open,
  patient,
  onClose,
}) => {
  const { items, loading, fetchItems, deductForService } = useInventoryContext();
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [rows, setRows] = useState<DeductionRow[]>([newRow(1)]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nextKey, setNextKey] = useState(2);

  useEffect(() => {
    if (!open) return;
    setRows([newRow(1)]);
    setNotes('');
    setNextKey(2);
    fetchItems();
  }, [open, fetchItems]);

  const availableItems = useMemo(() => items.filter((item) => item.quantity > 0), [items]);

  const updateRow = (key: number, changes: Partial<DeductionRow>) => {
    setRows((current) => current.map((row) => row.key === key ? { ...row, ...changes } : row));
  };

  const handleSubmit = async () => {
    const selectedRows = rows.filter((row) => row.inventoryId);
    if (selectedRows.length === 0) {
      message.warning(isArabic ? 'اختر صنفاً واحداً على الأقل' : 'Select at least one inventory item');
      return;
    }

    const selectedIds = selectedRows.map((row) => row.inventoryId as string);
    if (new Set(selectedIds).size !== selectedIds.length) {
      message.warning(isArabic ? 'لا يمكن اختيار نفس الصنف أكثر من مرة' : 'Each inventory item can only be selected once');
      return;
    }

    for (const row of selectedRows) {
      const item = items.find((candidate) => candidate._id === row.inventoryId);
      if (!item || row.quantity < 1 || row.quantity > item.quantity) {
        message.error(
          isArabic
            ? `الكمية غير متاحة للصنف: ${item?.name || ''}`
            : `The requested quantity is not available for ${item?.name || 'this item'}`
        );
        return;
      }
    }

    const patientId = patient?._id || patient?.patient_id;
    if (!patientId) {
      message.error(isArabic ? 'تعذر تحديد ملف المريض' : 'Could not identify this patient file');
      return;
    }

    try {
      setSubmitting(true);
      const result = await deductForService({
        patient_id: String(patientId),
        patient_name: patient?.patient_name || patient?.name || '',
        action_type: 'manual',
        consumables: selectedRows.map((row) => ({
          inventory_id: row.inventoryId as string,
          quantity: row.quantity,
          notes: notes.trim() || (isArabic ? 'خصم يدوي من ملف المريض' : 'Manual deduction from patient file'),
        })),
      });

      if (result.errors.length > 0) {
        const failedNames = result.errors.map((error: any) => error.name || error.inventory_id).join(', ');
        message.warning(
          isArabic
            ? `تم خصم ${result.results.length} صنف، وتعذر خصم: ${failedNames}`
            : `${result.results.length} item(s) deducted; could not deduct: ${failedNames}`
        );
      } else {
        message.success(isArabic ? 'تم خصم المخزون للمريض بنجاح' : 'Inventory deducted for this patient');
      }
      onClose();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
        (isArabic ? 'تعذر خصم المخزون. لم يتم تغيير النموذج.' : 'Inventory deduction failed. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isArabic ? 'خصم مخزون للمريض' : 'Deduct inventory for patient'}
      open={open}
      onCancel={submitting ? undefined : onClose}
      onOk={handleSubmit}
      okText={isArabic ? 'تأكيد الخصم' : 'Confirm deduction'}
      cancelText={isArabic ? 'إلغاء' : 'Cancel'}
      confirmLoading={submitting}
      okButtonProps={{ danger: true }}
      width={680}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={patient?.patient_name || patient?.name || (isArabic ? 'المريض الحالي' : 'Current patient')}
        description={
          isArabic
            ? 'اختر الأصناف والكميات المستخدمة لهذا المريض. سيتم تسجيل العملية في حركة المخزون.'
            : 'Select the items and quantities used for this patient. This will be recorded in inventory history.'
        }
      />

      {!loading && availableItems.length === 0 ? (
        <Empty description={isArabic ? 'لا توجد أصناف متاحة في المخزون' : 'No in-stock inventory items'} />
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {rows.map((row) => {
            const selectedItem = items.find((item) => item._id === row.inventoryId);
            return (
              <Row key={row.key} gutter={[8, 8]} align="top">
                <Col xs={24} sm={15}>
                  <Select
                    showSearch
                    loading={loading}
                    value={row.inventoryId}
                    placeholder={isArabic ? 'اختر صنفاً' : 'Select item'}
                    optionFilterProp="label"
                    onChange={(value) => updateRow(row.key, { inventoryId: value, quantity: 1 })}
                    options={availableItems.map((item) => ({
                      value: item._id,
                      label: `${item.name} — ${item.quantity} ${item.unit || ''} ${isArabic ? 'متاح' : 'available'}`,
                      disabled: rows.some((other) => other.key !== row.key && other.inventoryId === item._id),
                    }))}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col xs={20} sm={7}>
                  <InputNumber
                    min={1}
                    max={selectedItem?.quantity}
                    precision={0}
                    value={row.quantity}
                    disabled={!selectedItem}
                    onChange={(value) => updateRow(row.key, { quantity: Number(value) || 1 })}
                    style={{ width: '100%' }}
                    aria-label={isArabic ? 'الكمية' : 'Quantity'}
                  />
                  {selectedItem && (
                    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                      {isArabic ? 'المتاح' : 'Available'}: {selectedItem.quantity} {selectedItem.unit}
                    </Text>
                  )}
                </Col>
                <Col xs={4} sm={2} style={{ textAlign: 'end' }}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={isArabic ? 'حذف الصنف' : 'Remove item'}
                    disabled={rows.length === 1}
                    onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}
                  />
                </Col>
              </Row>
            );
          })}

          <Button
            type="dashed"
            icon={<PlusOutlined />}
            disabled={rows.length >= availableItems.length}
            onClick={() => {
              setRows((current) => [...current, newRow(nextKey)]);
              setNextKey((value) => value + 1);
            }}
            block
          >
            {isArabic ? 'إضافة صنف آخر' : 'Add another item'}
          </Button>

          <Input.TextArea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            maxLength={300}
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder={isArabic ? 'ملاحظة (اختياري)' : 'Note (optional)'}
          />

          <Text type="secondary" style={{ fontSize: 12 }}>
            {isArabic ? 'لن يتم الخصم إذا كانت الكمية المطلوبة أكبر من المتاح.' : 'An item will not be deducted if the requested quantity exceeds available stock.'}
          </Text>
        </Space>
      )}
    </Modal>
  );
};

export default ManualInventoryDeductionModal;
